import React, { useEffect, useState } from 'react'
import { Box, Checkbox, FormControlLabel, Stack } from '@mui/material'
import { com } from '@eidu/web'
import FieldValueEqualsInput from './FieldValueEqualsInput'
import { findEntityTypeByFieldId } from '../../util/EntityTypeExtensions'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector

type EntitySelectorInputProps = {
  entityTypes: readonly EntityType[]
  entitySelector: EntitySelector
  setEntitySelector: (selector: EntitySelector) => void
  disabled?: boolean
}

const TargetSelectorInput: React.FC<EntitySelectorInputProps> = ({
  entityTypes,
  entitySelector,
  setEntitySelector,
  disabled = false,
}) => {
  const isDisabled = disabled
  const [filter, setFilter] = useState(entitySelector instanceof EntitySelector.FieldValueEquals)
  const [entityTypeId, setEntityTypeId] = useState<EntityTypeId | undefined>()

  useEffect(() => {
    if (entitySelector instanceof EntitySelector.OfType) {
      setFilter(false)
      setEntityTypeId(entitySelector.typeId)
    } else if (entitySelector instanceof EntitySelector.FieldValueEquals) {
      setFilter(true)
      const newEntityTypeId = findEntityTypeByFieldId(entityTypes, entitySelector.fieldId)
      if (newEntityTypeId) setEntityTypeId(newEntityTypeId.id)
    } else {
      throw new Error(`Unsupported entity selector type for TargetSelector: ${entitySelector}`)
    }
  }, [entitySelector])

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ padding: 1, flex: 1, minWidth: 0 }}>
      {entityTypes && (
        <Box sx={{ flexShrink: 0 }}>
          <EntityTypeField
            label="Type"
            types={entityTypes}
            entityTypeId={entityTypeId}
            onChange={(newEntityType) => {
              setEntitySelector?.(new EntitySelector.OfType(newEntityType.id))
            }}
            disabled={disabled}
          />
        </Box>
      )}
      {entityTypeId && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={filter}
              onChange={(_, isChecked) => {
                if (!isChecked) {
                  setEntitySelector?.(new EntitySelector.OfType(entityTypeId))
                }
                setFilter(isChecked)
              }}
              sx={{ p: 0.25 }}
            />
          }
          label="Filter"
          slotProps={{ typography: { variant: 'body2' } }}
          sx={{ flexShrink: 0, mx: 0 }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {entityTypeId && filter && (
          <FieldValueEqualsInput
            disabled={isDisabled}
            entityTypeId={entityTypeId}
            selector={entitySelector as EntitySelector.FieldValueEquals}
            setSelector={(newSelector) => {
              if (newSelector) setEntitySelector?.(newSelector)
            }}
          />
        )}
      </Box>
    </Stack>
  )
}

export default TargetSelectorInput
